import { getGraphBearerToken } from './token-service';
import {
  getCurrentUserDetailsUrl,
  getProfileUpdateUrl,
  getProfileUrl,
  getUserActivityUrl,
  getUserStatusUrl,
} from './urls';
import { getSiteId } from 'lib/gql/getSiteId';

export interface BaseRegistrationRequest {
  Email: string;
  Subscriptions?: {
    SubscriptionId: string;
    Subscribed: boolean;
  }[];
  Questions?: {
    QuestionId: string;
    Answer: {
      AnswerId: string;
    };
  }[];
  TermsOfUseAcceptance?: string;
  CaptchaToken?: string;
  CultureName?: string;
  Consent?: {
    PrivacyPolicyAcceptance: string;
    EmailConsent: string;
    DataSharingConsent: string;
  };
}

export interface CredentialsRegistrationRequest extends BaseRegistrationRequest {
  type: 'credentials';
  Password: string;
  SourceCode?: string;
  BirthMonth: number;
  BirthYear: number;
  PostalCode: string;
}

export interface OAuthRegistrationRequest extends BaseRegistrationRequest {
  type: 'oauth';
  Password?: string;
  SourceCode?: string;
  BirthMonth: number;
  BirthYear: number;
  PostalCode: string;
  Federation: {
    Provider: string;
    ProviderUserId: string;
    ProviderUserToken: string;
  };
}

export type RegistrationRequest = CredentialsRegistrationRequest | OAuthRegistrationRequest;

export interface RegistrationError {
  IsValid: boolean;
  Message: string;
  RuleId: string;
  FieldId: string;
  FieldName: string;
}

export interface RegistrationResponse {
  UserId?: string;
  Status?: string;
  error?: string;
  error_description?: string;
  Errors?: RegistrationError[];
  bearerToken?: string;
  bearerTokenExpiresIn?: number;
  validationErrors?: ValidationError[];
}

export type ValidationErrorType =
  | 'EMAIL_REQUIRED'
  | 'PASSWORD_REQUIRED'
  | 'BIRTH_MONTH_YEAR_REQUIRED'
  | 'ZIP_CODE_REQUIRED'
  | 'MINIMUM_AGE_REQUIRED'
  | 'INVALID_ZIP_CODE'
  | 'EMAIL_ALREADY_EXISTS'
  | 'INVALID_CAPTCHA'
  | 'REGISTRATION_FAILED'
  // Profile rule validation errors
  | 'EMAIL_FORMAT'
  | 'EMAIL_PROFANITY'
  | 'FIRST_NAME_CHAR'
  | 'FIRST_NAME_PROFANITY'
  | 'LAST_NAME_CHAR'
  | 'LAST_NAME_PROFANITY'
  | 'EMAIL_DISPOSABLE'
  | 'EMAIL_ACCOUNT_INVALID'
  | 'EMAIL_ADDRESS_INVALID'
  | 'EMAIL_DOMAIN_INVALID'
  | 'EMAIL_ROLE_INVALID'
  | 'PROFILE_FAIL';

export interface ValidationError {
  error: ValidationErrorType;
  field: string;
  error_description?: string;
}

// Shared constants
export const CONSENT_STATUS = {
  PRIVACY_POLICY: 'CONFIRMED',
  EMAIL: 'CONFIRMED',
  DATA_SHARING: 'CONFIRMED',
  EMAIL_NOT_GIVEN: 'NOTGIVEN',
  EMAIL_OPT_OUT: 'OPT_OUT',
} as const;

export const FIELD_VALIDATION_ERRORS: ValidationErrorType[] = [
  'EMAIL_REQUIRED',
  'PASSWORD_REQUIRED',
  'BIRTH_MONTH_YEAR_REQUIRED',
  'ZIP_CODE_REQUIRED',
];

/**
 * Maps Profile RuleIds to ValidationErrorType and the associated form field.
 * The first 6 entries are Profile-only rules; the remaining 5 (email.verification.*)
 * are Briteverify-specific rules.
 */
export const PROFILE_RULE_ID_MAP: Record<string, { type: ValidationErrorType; field: string }> = {
  'email.format.rule': { type: 'EMAIL_FORMAT', field: 'email' },
  'email.profanity.rule': { type: 'EMAIL_PROFANITY', field: 'email' },
  'alpha.character.rule': { type: 'FIRST_NAME_CHAR', field: 'firstName' },
  'first.name.profanity.rule': { type: 'FIRST_NAME_PROFANITY', field: 'firstName' },
  'last.name.rule': { type: 'LAST_NAME_CHAR', field: 'lastName' },
  'last.name.profanity.rule': { type: 'LAST_NAME_PROFANITY', field: 'lastName' },
  'email.verification.disposable.rule': { type: 'EMAIL_DISPOSABLE', field: 'email' },
  'email.verification.account.invalid.rule': { type: 'EMAIL_ACCOUNT_INVALID', field: 'email' },
  'email.verification.address.invalid.rule': { type: 'EMAIL_ADDRESS_INVALID', field: 'email' },
  'email.verification.domain.invalid.rule': { type: 'EMAIL_DOMAIN_INVALID', field: 'email' },
  'email.verification.role.address.rule': { type: 'EMAIL_ROLE_INVALID', field: 'email' },
};

const ERROR_RULE_MAPPINGS = {
  EMAIL: {
    ruleIds: ['unique.email.rule'],
    fieldNames: ['email'],
    ruleIdPatterns: ['email', 'unique', 'format', 'valid'],
  },
  AGE: {
    ruleIds: ['minimum.age.rule'],
    fieldNamePatterns: ['birth'],
    ruleIdPatterns: ['age'],
  },
  ZIPCODE: {
    ruleIds: ['zip.code.rule'],
    fieldNamePatterns: ['zip', 'postal'],
  },
} as const;

/**
 * Gets the default terms of use acceptance timestamp
 */
function getDefaultTermsOfUseAcceptance(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Validates required fields for registration
 * @param registrationData - The registration credentials to validate
 * @returns Array of validation errors, empty if all validations pass
 */
export function validateRegistrationFields(
  registrationData: RegistrationRequest
): ValidationError[] {
  const validationErrors: ValidationError[] = [];

  if (!registrationData?.Email) {
    validationErrors.push({
      error: 'EMAIL_REQUIRED',
      field: 'email',
    });
  }

  if (registrationData?.type === 'credentials' && !registrationData?.Password) {
    validationErrors.push({
      error: 'PASSWORD_REQUIRED',
      field: 'password',
    });
  }

  if (
    registrationData?.type === 'credentials' &&
    (registrationData?.BirthMonth ?? registrationData?.BirthYear) !== undefined &&
    (!registrationData?.BirthMonth || !registrationData?.BirthYear)
  ) {
    validationErrors.push({
      error: 'BIRTH_MONTH_YEAR_REQUIRED',
      field: 'birthdate',
    });
  }

  if (
    registrationData?.type === 'oauth' &&
    (registrationData?.BirthMonth ?? registrationData?.BirthYear) !== undefined &&
    (!registrationData?.BirthMonth || !registrationData?.BirthYear)
  ) {
    validationErrors.push({
      error: 'BIRTH_MONTH_YEAR_REQUIRED',
      field: 'birthdate',
    });
  }

  if (!registrationData?.PostalCode) {
    validationErrors.push({
      error: 'ZIP_CODE_REQUIRED',
      field: 'zipcode',
    });
  }

  return validationErrors;
}

/**
 * Checks if an error matches email validation patterns
 */
function isEmailError(error: RegistrationError): boolean {
  const fieldName = error.FieldName?.toLowerCase() || '';
  const ruleId = error.RuleId?.toLowerCase() || '';

  const isEmailRuleId = ERROR_RULE_MAPPINGS.EMAIL.ruleIds.some((id) => ruleId === id);
  const isEmailField = fieldName === 'email';
  const hasEmailPattern = ERROR_RULE_MAPPINGS.EMAIL.ruleIdPatterns.some((pattern) =>
    ruleId.includes(pattern)
  );

  return isEmailRuleId || (isEmailField && hasEmailPattern);
}

/**
 * Checks if an error matches age validation patterns
 */
function isAgeError(error: RegistrationError): boolean {
  const fieldName = error.FieldName?.toLowerCase() || '';
  const ruleId = error.RuleId?.toLowerCase() || '';

  return (
    ERROR_RULE_MAPPINGS.AGE.ruleIds.some((id) => ruleId === id) ||
    (ERROR_RULE_MAPPINGS.AGE.fieldNamePatterns.some((pattern) => fieldName.includes(pattern)) &&
      ERROR_RULE_MAPPINGS.AGE.ruleIdPatterns.some((pattern) => ruleId.includes(pattern)))
  );
}

/**
 * Checks if an error matches zipcode validation patterns
 */
function isZipcodeError(error: RegistrationError): boolean {
  const fieldName = error.FieldName?.toLowerCase() || '';
  const ruleId = error.RuleId?.toLowerCase() || '';

  return (
    ERROR_RULE_MAPPINGS.ZIPCODE.ruleIds.some((id) => ruleId === id) ||
    ERROR_RULE_MAPPINGS.ZIPCODE.fieldNamePatterns.some((pattern) => fieldName.includes(pattern))
  );
}

/**
 * Validates and transforms API response errors into a consistent format
 * @param response - The registration response from the API
 * @returns Array of validation errors, empty if no errors found
 */
export function validateRegistrationResponse(response: RegistrationResponse): ValidationError[] {
  if (!response.Errors || !Array.isArray(response.Errors) || response.Errors.length === 0) {
    return [];
  }

  const ruleValidationErrors: ValidationError[] = [];

  // Partition errors: Profile/Briteverify rule IDs are handled by PROFILE_RULE_ID_MAP first.
  // Remaining errors fall through to the legacy pattern-based checks below.
  const unmappedErrors: RegistrationError[] = [];

  response.Errors.forEach((profileError) => {
    const ruleId = profileError.RuleId?.toLowerCase() || '';
    const mapping = PROFILE_RULE_ID_MAP[ruleId];
    if (mapping) {
      ruleValidationErrors.push({
        error: mapping.type,
        field: mapping.field,
        error_description: profileError.Message,
      });
    } else {
      unmappedErrors.push(profileError);
    }
  });

  // Legacy pattern-based checks only run on errors not already handled above.

  // Email validation — unique email / duplicate account (e.g. unique.email.rule)
  const emailError = unmappedErrors.find(isEmailError);
  if (emailError) {
    ruleValidationErrors.push({
      error: 'EMAIL_ALREADY_EXISTS',
      field: 'email',
      error_description: emailError.Message,
    });
  }

  // Age validation
  const ageError = unmappedErrors.find(isAgeError);
  if (ageError) {
    ruleValidationErrors.push({
      error: 'MINIMUM_AGE_REQUIRED',
      field: 'birthdate',
      error_description: ageError.Message,
    });
  }

  // Zipcode validation
  const zipcodeError = unmappedErrors.find(isZipcodeError);
  if (zipcodeError) {
    ruleValidationErrors.push({
      error: 'INVALID_ZIP_CODE',
      field: 'zipcode',
      error_description: zipcodeError.Message,
    });
  }

  // Catch-all: any remaining unmapped errors that don't match the legacy checks fall back to PROFILE_FAIL.
  const hasUnhandledErrors = unmappedErrors.some(
    (profileError) =>
      !isEmailError(profileError) && !isAgeError(profileError) && !isZipcodeError(profileError)
  );
  if (hasUnhandledErrors) {
    ruleValidationErrors.push({
      error: 'PROFILE_FAIL',
      field: 'form',
    });
  }

  return ruleValidationErrors;
}

/**
 * Determines the HTTP status code based on validation error types
 * @param errors - Array of validation errors
 * @returns HTTP status code (400 for field validation, 409 for business rule errors)
 */
export function getValidationStatusCode(errors: ValidationError[]): number {
  if (!errors || errors.length === 0) {
    return 400;
  }

  const isFieldValidation = errors.some((err) => FIELD_VALIDATION_ERRORS.includes(err.error));
  return isFieldValidation ? 400 : 409;
}

/**
 * Builds the request payload based on registration type
 */
function buildRequestPayload(registrationData: RegistrationRequest): Record<string, unknown> {
  const basePayload: Record<string, unknown> = {
    Email: registrationData.Email,
    PostalCode: registrationData.PostalCode,
    ...(registrationData.SourceCode && { SourceCode: registrationData.SourceCode }),
    CaptchaToken: registrationData.CaptchaToken,
    ...(registrationData.Questions?.length ? { Questions: registrationData.Questions } : {}),
    ...(registrationData.CultureName && { CultureName: registrationData.CultureName }),
  };

  if (registrationData.type === 'credentials') {
    return {
      ...basePayload,
      Password: registrationData.Password,
      BirthMonth: registrationData.BirthMonth,
      BirthYear: registrationData.BirthYear,
      Consent: registrationData.Consent,
      TermsOfUseAcceptance:
        registrationData.TermsOfUseAcceptance || getDefaultTermsOfUseAcceptance(),
    };
  }

  return {
    ...basePayload,
    BirthMonth: registrationData.BirthMonth,
    BirthYear: registrationData.BirthYear,
    Federation: registrationData.Federation,
    Consent: registrationData.Consent,
    TermsOfUseAcceptance: registrationData.TermsOfUseAcceptance || getDefaultTermsOfUseAcceptance(),
  };
}

/**
 * Makes the registration API call
 */
async function callRegistrationAPI(
  registrationUrl: string,
  bearerToken: string,
  requestPayload: Record<string, unknown>
): Promise<Response> {
  return fetch(registrationUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify(requestPayload),
  });
}

/**
 * Processes the registration API response
 */
function processRegistrationResponse(
  response: Response,
  responseData: RegistrationResponse
): RegistrationResponse {
  if (!response.ok) {
    return {
      error: 'REGISTRATION_FAILED',
      error_description: response.statusText,
    };
  }

  const ruleValidationErrors = validateRegistrationResponse(responseData);
  if (ruleValidationErrors.length > 0) {
    return {
      ...responseData,
      validationErrors: ruleValidationErrors,
    };
  }

  return responseData;
}

export const getUserActivity = async (email: string) => {
  const siteId = await getSiteId();
  if (!siteId) {
    throw new Error('Site ID not found');
  }

  const bearerToken = await getGraphBearerToken(siteId);
  if (!bearerToken) {
    throw new Error('Failed to get bearer token');
  }
  const userActivityUrl = getUserActivityUrl(email);
  const response = await fetch(userActivityUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
  });

  return response.json();
};

export const getCurrentUserDetails = async (userId: string) => {
  const siteId = await getSiteId();
  if (!siteId) {
    throw new Error('Site ID not found');
  }
  const bearerToken = await getGraphBearerToken(siteId);
  if (!bearerToken) {
    throw new Error('Failed to get bearer token');
  }
  const currentUserDetailsUrl = getCurrentUserDetailsUrl(userId);
  const response = await fetch(currentUserDetailsUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
  });
  return response.json();
};

/**
 * Registers a new user with the external API
 * @param registrationData - The registration data (credentials or OAuth)
 * @param failTrigger - Whether to throw errors or return error response (default: true)
 * @returns Registration response or null on failure
 */
export async function registerUser(
  registrationData: RegistrationRequest,
  failTrigger = true
): Promise<RegistrationResponse | null> {
  try {
    // Validate input fields before making API call
    const fieldValidationErrors = validateRegistrationFields(registrationData);
    if (fieldValidationErrors.length > 0) {
      return {
        error: 'REGISTRATION_FAILED',
        validationErrors: fieldValidationErrors,
      };
    }

    // Get site ID and build registration URL
    const siteId = await getSiteId();
    const profileUrl = getProfileUrl();
    const registrationUrl = `${profileUrl}/users`;

    // Build request payload
    const requestPayload = buildRequestPayload(registrationData);

    // Add subscriptions if provided
    if (registrationData.Subscriptions && registrationData.Subscriptions.length > 0) {
      requestPayload.Subscriptions = registrationData.Subscriptions;
    }

    // Get bearer token for authentication
    const bearerToken = await getGraphBearerToken(siteId);
    if (!bearerToken) {
      throw new Error('Failed to obtain bearer token');
    }

    // Make registration API call
    const response = await callRegistrationAPI(registrationUrl, bearerToken, requestPayload);
    const responseData = await response.json();

    // Process and return response
    return processRegistrationResponse(response, responseData);
  } catch (error) {
    console.error('Error registering user:', error);
    if (failTrigger) {
      throw new Error(`Error registering user: ${error}`);
    }
    return {
      error: 'REGISTRATION_FAILED',
      error_description: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * User status response from the status API
 */
export interface UserStatusResponse {
  UserId: string;
  Status: 'Email' | 'User';
}

/**
 * Federation data for linking social accounts
 */
export interface FederationData {
  Provider: string;
  // ProviderUserId: string;
  ProviderUserToken: string;
}

/**
 * Gets user status by email to determine if they are a registered user or just newsletter subscriber
 * @param email - The user's email address
 * @returns UserStatusResponse or null if not found/error
 */
export const getUserStatus = async (email: string): Promise<UserStatusResponse | null> => {
  try {
    const siteId = await getSiteId();
    if (!siteId) {
      console.error('Site ID not found');
      return null;
    }

    const bearerToken = await getGraphBearerToken(siteId);
    if (!bearerToken) {
      console.error('Failed to get bearer token');
      return null;
    }

    const statusUrl = getUserStatusUrl(email);
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    if (!response.ok) {
      console.error(`Error fetching user status: ${response.statusText}`);
      return null;
    }

    const data = await response.json();
    return data as UserStatusResponse;
  } catch (error) {
    console.error('Error getting user status:', error);
    return null;
  }
};

/**
 * Updates user profile with federation data to link social account
 * @param accessToken - The user's access token (obtained after credential login)
 * @param federation - The federation data to add
 * @returns boolean indicating success
 */
export const updateUserProfileWithFederation = async (
  accessToken: string,
  federation: FederationData
): Promise<boolean> => {
  try {
    const updateUrl = getProfileUpdateUrl();

    const response = await fetch(updateUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        Federation: federation,
      }),
    });

    if (!response.ok) {
      console.error(`Error updating user profile with federation: ${response.statusText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating user profile with federation, catch block:', error);
    return false;
  }
};
